import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { StatusCode } from "api/protocols";
import Card from "components/card/Card";
import Page from "components/page/Page";
import Sheet from "components/sheet/Sheet";
import Checkbox from "components/form/checkbox/Checkbox";
import Input from "components/form/input/Input";
import "./inventory.scss";
import { showError, showSuccess } from "redux/actions/ui";
import {
  ExportInventoryLogs,
  GetInventoryLog,
  GetInventoryRecord,
  PrintInventoryLogs,
  PutInventoryRecord,
} from "api/rpc/2024-04/facilityAdmin/product/inventory";
import { useParams } from "react-router";
import moment, { MomentInput } from "moment";
import { Select } from "components/select/index";

import { useAppDispatch } from "hooks/redux";
import DataTable from "../../../../../components/dataTable/DataTable";
import axios, { CancelToken } from "axios";
import useModal from "hooks/modals/useModal";
import { TProductVendor } from "redux/reducers/models/product";
import { GetVendor } from "api/rpc/2024-04/facilityAdmin/product/vendor";
import { isEqualWith, isNull } from "lodash";
import ReactDOM from "react-dom";
import Icon from "components/icon/Icon";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { PutVariantLabels } from "api/rpc/2024-04/facilityAdmin/product/product";
import { LocaleCurrency } from "helpers/Locale";
import { displayCurrency, downloadCsv, formatDate, openPrintWindow } from "helpers/Helpers";
import Toggle from "components/form/toggle/Toggle";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import { GetPurchaseOrder } from "api/rpc/2024-04/facilityAdmin/order/purchaseOrder";
import { IPurchaseOrder } from "redux/reducers/models/purchaseOrder";
import FormLayout from "components/form/FormLayout";

type TPrintModalTemplates = {
  id: number;
  title: string;
};

interface IVendorState {
  vendors: Array<TProductVendor>;
  searchedVendors: Array<TProductVendor>;
  searching: boolean;
  vendorQuery: string;
  vendor: TProductVendor;
  packing_slip_number: string;
  po_number: string;
  processed_at: Date;
}

export default function InventoryLog() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [record, setRecord] = useState<Record<string, any>>(undefined);
  const [state, setState] = useState({
    inventory: undefined as Record<string, any>,
    selectedItems: [] as boolean[],
    totalCost: 0,
    totalQuantity: 0,
    totalOrderedQuantity: 0,
  });

  const [purchaseOrder, setPurchaseOrder] = useState<IPurchaseOrder>(null);

  const { state: printModal, updateModal } = useModal({
    clearOnClose: false,
    templates: [{ id: 5160, title: t("Avery 5160") }, { id: "zebra_2x1", title: t("Zebra 2x1") }] as TPrintModalTemplates[], //This will be changed when we load templates
    selectedTemplateId: 5160,
    offset: 0,
    hideBarcode: false,
  });

  const [vendorState, setVendorState] = useState<IVendorState>({
    vendorQuery: "",
    searching: false,
    vendors: [],
    searchedVendors: [],
    vendor: null,
    packing_slip_number: "",
    po_number: "",
    processed_at: new Date(),
  });

  const [vendorStateBeforeChange, setVendorStateBeforeChanges] = useState<TProductVendor>(undefined);
  const [inventoryRecordsBeforeChange, setInventoryRecordsBeforeChange] = useState(undefined);
  const [recordBeforeState, setRecordBeforeState] = useState({
    processed_at: new Date(),
  });
  // const [packingSlipBeforeChange, setPackingSlipBeforeChanges] = useState(undefined);

  const { Option } = Select;

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadInventoryRecord(Number(id), source.token); // Ony using `processed_at` & `admin_user_full_name`
    void loadInventoryLog(Number(id), source.token);
    return () => source.cancel();
  }, [id]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: NodeJS.Timeout = null;
    if (mounted === true) {
      timeoutId = global.setTimeout(() => {
        void searchVendors(vendorState.vendorQuery, source.token);
      }, 300);
    }
    return () => {
      source.cancel();
      mounted = false;
      clearTimeout(timeoutId);
      setVendorState(prevState => ({ ...prevState, searchedVendors: [], searching: false, vendor: null }));
    };
  }, [vendorState.vendorQuery]);

  async function searchVendors(vendorQuery: string, token: CancelToken) {
    if (vendorQuery === "") {
      setVendorState(prevState => ({ ...prevState, searchedVendors: [], searching: false }));
      return;
    }

    setVendorState(prevState => ({ ...prevState, searching: true }));
    const vendorRes = await GetVendor({ search: vendorState.vendorQuery }, false, token);
    if (token && token.reason) {
      return;
    }
    if (vendorRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting vendors")));
      return;
    }
    setVendorState(prevState => ({ ...prevState, searchedVendors: vendorRes?.data, searching: false }));
  }

  async function loadInventoryRecord(id: number, token?: CancelToken) {
    const inventoryRes = await GetInventoryRecord({ id }, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (inventoryRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting inventory record")));
      return;
    }

    setRecord(inventoryRes.data as Record<string, any>);

    const vendorRes = await GetVendor(null, true);
    if (vendorRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error retrieving vendor list")));
      return;
    }

    let purchaseOrder = null;
    let totalOrderedQuantity = 0;
    if (inventoryRes?.data?.purchase_order_id) {
      const purchaseOrderRes = await GetPurchaseOrder({id: inventoryRes?.data?.purchase_order_id, extended: true}, true);
      if (purchaseOrderRes.status !== StatusCode.OK) {
        dispatch(showError(t("Error retrieving purchase order")));
        return;
      }
      purchaseOrder = purchaseOrderRes?.data[0];
      totalOrderedQuantity = purchaseOrder?.items?.reduce((totalQuantity: number, item) => {
        return (item?.quantity) + totalQuantity;
      }, 0);
    }

    let vendor: TProductVendor = null;
    const vendorId = inventoryRes?.data?.vendor_id;
    if (vendorId) {
      vendor = vendorRes?.data?.find(vendor => vendor?.id === vendorId);
    }

    

    ReactDOM.unstable_batchedUpdates(() => {
      setVendorState(prevState => ({
        ...prevState,
        vendors: vendorRes.data,
        vendor: vendor ?? null,
        packing_slip_number: inventoryRes.data.packing_slip_number,
        po_number: inventoryRes.data.po_number,
        processed_at: new Date(moment(inventoryRes?.data?.processed_at as string)?.local()?.toISOString()),
      }));
      setPurchaseOrder(purchaseOrder);
      setState(prev => ({...prev, totalOrderedQuantity}));
    });
  }

  async function loadInventoryLog(inventory_record_id: number, token?: CancelToken) {
    if (state.inventory !== undefined) {
      setState(prev => ({ ...prev, inventory: undefined, selectedItems: [] }));
    }

    const inventoryRes = await GetInventoryLog({ inventory_record_id }, token ? false : true, token);

    console.log(inventoryRes);
    if (token && token.reason) {
      return;
    }
    if (inventoryRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting inventory log")));
      setState(prev => ({ ...prev, inventory: [], selectedItems: [] }));
      return;
    }

    const totalCost = inventoryRes.data?.reduce((totalCost: number, item: Record<string, any>) => {
      return item?.cost * item?.change + totalCost;
    }, 0);

    const totalQuantity = inventoryRes?.data?.reduce((totalQuantity: number, item: Record<string, any>) => {
      return (item?.change as number) + totalQuantity;
    }, 0);

    setState(prev => ({
      ...prev,
      inventory: inventoryRes.data,
      selectedItems: new Array(inventoryRes.data.length).fill(true),
      totalCost: totalCost,
      totalQuantity: totalQuantity,
    }));
  }

  async function handlePrint() {
    updateModal({ isOpen: false });

    const variants = state.inventory
      .filter((data: any, index: number) => state.selectedItems[index])
      .map((variant: any) => ({
        variant_id: variant.inventory_item.variant_id,
        quantity: variant.change,
      }));

    const getVariantLabelsResponse = await PutVariantLabels(
      {
        offset: printModal.offset,
        variants: variants,
        template_id: printModal.selectedTemplateId,
        hide_barcode: printModal.hideBarcode,
      },
      true,
    );

    if (getVariantLabelsResponse.status !== StatusCode.OK) {
      dispatch(showError(t("Failed to get the variant labels")));
      return;
    }

    window.open().document.write(getVariantLabelsResponse.data as string);
  }

  function handleCloseModal() {
    updateModal({
      isOpen: false,
      offset: 0,
      selectedTemplateId: 5160,
    });
    setState(prev => ({ ...prev, selectedItems: new Array(prev.selectedItems.length).fill(true) }));
  }

  function handleCheckBoxChange(index: number) {
    const updatedCheckBoxes = state.selectedItems;
    updatedCheckBoxes[index] = !updatedCheckBoxes[index];
    setState(prev => ({ ...prev, selectedItems: updatedCheckBoxes }));
  }
  const primaryAction = {
    content: t("Print Labels"),
    action: () => updateModal({ isOpen: true }),
  };

  function unsavedChangesExist() {
    if (vendorStateBeforeChange === undefined) {
      if (vendorState?.vendors?.length > 0) {
        setVendorStateBeforeChanges(vendorState?.vendor);
        setRecordBeforeState(prev => ({...prev, processed_at: new Date (moment(vendorState?.processed_at)?.local()?.toISOString())}));
      }
      return false;
    }

    if (inventoryRecordsBeforeChange === undefined) {
      if (vendorState) {
        setInventoryRecordsBeforeChange({
          packing_slip_number: vendorState?.packing_slip_number,
          po_number: vendorState?.po_number,
        });
      }
      return false;
    }

    return (
      !isEqualWith(vendorStateBeforeChange, vendorState?.vendor, (originalValue, newValue) => {
        if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
          return true;
        }
      }) ||
      !isEqualWith(
        inventoryRecordsBeforeChange.packing_slip_number,
        vendorState?.packing_slip_number,
        (originalValue, newValue) => {
          if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
            return true;
          }
        },
      ) ||
      !isEqualWith(inventoryRecordsBeforeChange.po_number, vendorState?.po_number, (originalValue, newValue) => {
        if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
          return true;
        }
      })
      ||
      !isEqualWith(recordBeforeState?.processed_at, vendorState.processed_at, (originalValue, newValue) => {
        if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
          return true;
        }
      })
    );
  }

  function cancelUnsavedChanges() {
    setVendorState(prevState => ({
      ...prevState,
      vendor: vendorStateBeforeChange,
      packing_slip_number: inventoryRecordsBeforeChange.packing_slip_number,
      po_number: inventoryRecordsBeforeChange.po_number,
      processed_at: recordBeforeState?.processed_at,
    }));
  }

  async function updateInventoryRecord() {
    const formattedDate = formatDate(vendorState?.processed_at);
    const finalDate = formattedDate?.concat(` ${(record?.processed_at as string)?.split(" ")[1]}`);
    const inventoryRes = await PutInventoryRecord(
      {
        id: Number(id),
        vendor_id: vendorState?.vendor?.id ?? null,
        packing_slip_number: vendorState.packing_slip_number,
        po_number: vendorState?.po_number,
        processed_at: finalDate,
      },
      true,
    );
    if (inventoryRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating inventory record")));
      return;
    }
    dispatch(showSuccess(t("Successfully updated inventory record")));
    ReactDOM.unstable_batchedUpdates(() => {
      setVendorStateBeforeChanges(vendorState?.vendor);
      setInventoryRecordsBeforeChange({
        packing_slip_number: vendorState.packing_slip_number,
        po_number: vendorState.po_number,
      });
      setRecordBeforeState(prev => ({...prev, processed_at: new Date (moment(vendorState?.processed_at)?.local()?.toISOString())}));
      setRecord(prev => ({...prev, processed_at: inventoryRes?.data?.processed_at ?? prev?.processed_at}));
    });
  }

  function displayVendors() {
    if ((vendorState?.searching && vendorState?.vendorQuery) || vendorState?.searchedVendors?.length > 0) {
      return [...(vendorState?.searchedVendors ?? [])];
    }
    return [...(vendorState?.vendors ?? [])];
  }

  function handleVendorChange(value: number, vendor: TProductVendor) {
    setVendorState(prevState => ({ ...prevState, vendor }));
  }

  function handlePackingSlipChange(event: any) {
    const value = event?.target?.value;

    if (value === undefined) {
      return;
    }

    setVendorState(prevState => ({ ...prevState, packing_slip_number: value }));
  }

  async function printInventoryReceiving() {
    const res = await PrintInventoryLogs({ inventory_record_id: Number(id) }, true);
    if (res?.status !== StatusCode.OK) {
      dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error printing inventory receiving")));
      return;
    }
    void openPrintWindow(res?.data as string);
  }

  async function exportInventoryReceiving() {
    const res = await ExportInventoryLogs({ inventory_record_id: Number(id) }, true);
    if (res?.status !== StatusCode.OK) {
      dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error exporting inventory receiving")));
      return;
    }
    void downloadCsv(res.data as string, `INVENTORY_RECEIVING_${moment(record?.processed_at as string).format("YYYY-MM-DD").toString()}`
      .replace(/ /g, "_")
      .toLowerCase() + '.csv');
  }

  return (
    <Page
      title={record ? `${moment(record.processed_at?.split(" ")[0] as MomentInput).format("LL")}` : "Loading.."}
      subtitle={record ? `${record.admin_user_full_name as string}` : ""}
      narrow
      primaryAction={primaryAction}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to Inventory"),
          url: "/admin/settings/inventory",
        },
      ]}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: () => void updateInventoryRecord(),
        onCancel: cancelUnsavedChanges,
      }}
      multipleActionDropdownAction={{
        label: t("Options"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("Print"),
              icon: "print",
              handler: () => printInventoryReceiving(),
            },
            {
              type: "handler",
              label: t("Export"),
              icon: "file-export",
              handler: () => exportInventoryReceiving(),
            },
          ],
        },
      }}
    >
      <Card>
        <Card.Section>
          {vendorState?.vendor ? (
            <>
              <div>
                <p className="inventory-vendor-label">{t("Vendor")}</p>
              </div>
              <div className="vendor-name">
                <div>{vendorState?.vendor?.title}</div>
                <button
                  onClick={() =>
                    setVendorState(prevState => ({
                      ...prevState,
                      vendor: null,
                      searchedVendors: [],
                      vendorQuery: "",
                    }))
                  }
                >
                  <Icon icon={faTimes} />
                </button>
              </div>
            </>
          ) : (
            <Select
              usePortal
              label={t("Vendor")}
              onChange={(value: number, vendor: TProductVendor) => handleVendorChange(value, vendor)}
              showSearch
              searchValue={vendorState.vendorQuery}
              searching={vendorState.searching}
              onSearch={(query: string) => setVendorState(prevState => ({ ...prevState, vendorQuery: query }))}
              showDropDownOnFocus
              placeholder={t("Search vendors...")}
            >
              {displayVendors()?.map((vendor, index) => {
                return (
                  <Option key={index} value={vendor?.id} extraValues={vendor}>
                    {vendor?.title}
                  </Option>
                );
              })}
            </Select>
          )}

          <div className="mt-4">
            <Input
              value={vendorState.packing_slip_number}
              id="packing_slip_number"
              label={t("Packing Slip")}
              placeholder="012345"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePackingSlipChange(e)}
            />
          </div>

          <div className="mt-4">
            <Input
              value={vendorState.po_number}
              id="po_number"
              label={t("PO Number")}
              placeholder="012345"
              onChange={e => setVendorState(prevState => ({ ...prevState, po_number: e.target.value }))}
            />
          </div>

          <div className="mt-4">
          <DatePickerInput
              months={1}
              position="left"
              startingDate={vendorState.processed_at}
              setStartingDate={(date: Date) => setVendorState(prevState => ({ ...prevState, processed_at: date }))}
              label={t("Process Date")}
            />
          </div>
        </Card.Section>
        {purchaseOrder ? (
          <Card.Section title={t("Purchase order")} sectionTitleActions={[{content: "View", action: () => window.open(`/admin/settings/purchase-orders/${purchaseOrder?.id}`, "__blank")}]}>
            <FormLayout>
              <FormLayout.Group>
                <div>
                  <p className="inventory-purchase-order-title">{t("PO number")}</p>
                  <p className="text-semibold">{purchaseOrder?.po_number}</p>
                </div>
                <div>
                  <p className="inventory-purchase-order-title">{t("Vendor")}</p>
                  <p className="text-semibold">{purchaseOrder?.vendor_name}</p>
                </div>
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
        ) : null}
        <Card.Section table="true">
          <DataTable
            columns={[
              { label: t("Product") },
              { label: t("Quantity"), justify: "right" },
              ...(purchaseOrder ? [{ label: t("Ordered Quantity"), justify: "right" }] : [] as any),
              { label: t("Unit Cost"), justify: "right" },
              { label: t("Total Cost"), justify: "right" },
            ]}
            secondaryColumns={[
              { label: "" },
              { label: `${state?.totalQuantity ?? 0}`, justify: "right" },
              ...(purchaseOrder ? [{ label: `${state?.totalOrderedQuantity ?? 0}`, justify: "right" }] : [] as any),
              { label: ``, justify: "right" },
              { label: `${displayCurrency("cad", state.totalCost ?? 0)}`, justify: "right" },
            ]}
            loading={state.inventory === undefined}
          >
            {state.inventory?.map((inventoryRecord: Record<string, any>, index: number) => {
              const foundItem = purchaseOrder ? purchaseOrder?.items?.find(item => item?.variant_id === inventoryRecord?.inventory_item?.variant?.id) : null;
              return (
                <tr key={index} className="inventory-receiving-table-row">
                  <td className="table-cell-lead-with-support-text inventory-receiving-table-row-left">
                    <p className="table-cell-lead-text">{inventoryRecord?.inventory_item?.variant?.product?.title}</p>
                    {inventoryRecord?.inventory_item?.variant?.product?.title !==
                      inventoryRecord?.inventory_item?.variant?.title && (
                        <p className="table-cell-support-text">{inventoryRecord?.inventory_item?.variant?.title}</p>
                      )}
                  </td>
                  <td>{inventoryRecord?.change}</td>
                  {purchaseOrder ? <td>{foundItem?.quantity ?? "-"} 
                    {/* {foundItem?.quantity ? <span style={{color: "red"}}>{`(${Number(inventoryRecord?.change) - Number(foundItem?.quantity) > 0 ? "+" : ""}${Number(inventoryRecord?.change) - Number(foundItem?.quantity)})`}</span> : null} */}
                    </td> : null}
                  <td>
                    <LocaleCurrency
                      currency="cad"
                      amount={
                        inventoryRecord?.cost
                          ? inventoryRecord?.cost
                          : 0
                      }
                    />
                  </td>
                  <td>
                    <LocaleCurrency
                      currency="cad"
                      amount={(inventoryRecord?.cost ?? 0) * (inventoryRecord?.change ?? 0)}
                    />
                  </td>
                </tr>
              );
            })}
          </DataTable>
        </Card.Section>
      </Card>
      <Sheet
        size="small"
        open={printModal.isOpen}
        okText={t("Print")}
        title={t("Print Labels")}
        onCancel={handleCloseModal}
        onOk={handlePrint}
        okDisabled={state.selectedItems.every(bool => !bool) || printModal.selectedTemplateId === -1}
        closable
        stacked
      >
        <Card>
          <Card.Section>
            <Select
              label={t("Label Template")}
              className="flex justify-center align-center w-full h-10 position-relative z-20 text-black text-medium appearance-none border-none focus:outline-none placeholder-gray-200 overflow"
              placeholder={t("Label Template")}
              defaultValue={printModal.templates[0].id}
              onChange={(id: number) => updateModal({ selectedTemplateId: id })}
            >
              <Select.Option value={-1}>{t('None Selected')}</Select.Option>
              {printModal.templates.length > 0 &&
                printModal.templates.map(template => {
                  return (
                    <Select.Option key={template.id} value={template.id}>
                      {template.title}
                    </Select.Option>
                  );
                })}
            </Select>
            <div style={{ margin: "10px" }} />
            <Input
              label={t("Offset")}
              value={printModal.offset}
              onChange={(event: any) => updateModal({ offset: event.target.value })}
              type="number"
            />
          </Card.Section>
          <Card.Section>
            {state.inventory?.map((inventoryRecord: Record<string, any>, index: number) => {
              return (
                <div key={index}>
                  <div className="flex flex-row gap-4 p-2 border-b border-gray-200">
                    <div className="text-regular">
                      <Checkbox
                        size={"medium"}
                        checked={state.selectedItems[index]}
                        onChange={() => handleCheckBoxChange(index)}
                      />
                    </div>
                    <div className="table-cell-lead-text">
                      {inventoryRecord?.inventory_item?.variant?.product?.title}
                    </div>
                    {inventoryRecord?.inventory_item?.variant?.product?.title !==
                      inventoryRecord?.inventory_item?.variant?.title && (
                        <p className="table-cell-support-text">{inventoryRecord?.inventory_item?.variant?.title}</p>
                      )}
                  </div>
                </div>
              );
            })}
          </Card.Section>
        </Card>
        <br />
        <Toggle
          checked={printModal.hideBarcode}
          onChange={() => updateModal({ hideBarcode: !printModal.hideBarcode })}
          label={t("Hide Barcode")}
        />
      </Sheet>
    </Page>
  );
}
